// redux/slices/mlmTreeSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

// -------------------- Async Thunks --------------------

export const fetchTree = createAsyncThunk(
  "mlmTree/fetchTree",
  async ({ companyId, serviceId, productId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (serviceId) params.append("service_id", serviceId);
      if (productId) params.append("product_id", productId);

      const res = await api.get(`/api/admin/mlm-tree/${companyId}?${params.toString()}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to fetch tree" }
      );
    }
  }
);

export const fetchFlatTree = createAsyncThunk(
  "mlmTree/fetchFlatTree",
  async ({ companyId, serviceId, productId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (serviceId) params.append("service_id", serviceId);
      if (productId) params.append("product_id", productId);

      const res = await api.get(`/api/admin/mlm-tree/${companyId}/flat?${params.toString()}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to fetch flat tree" }
      );
    }
  }
);

export const fetchAvailableUsers = createAsyncThunk(
  "mlmTree/fetchAvailableUsers",
  async ({ companyId, serviceId, productId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (serviceId) params.append("service_id", serviceId);
      if (productId) params.append("product_id", productId);

      const res = await api.get(
        `/api/admin/mlm-tree/${companyId}/available-users?${params.toString()}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to fetch available users" }
      );
    }
  }
);

export const assignNode = createAsyncThunk(
  "mlmTree/assignNode",
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/admin/mlm-tree/assign", data);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to assign node" }
      );
    }
  }
);

export const updateNode = createAsyncThunk(
  "mlmTree/updateNode",
  async ({ nodeId, data }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/api/admin/mlm-tree/${nodeId}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to update node" }
      );
    }
  }
);

export const removeNode = createAsyncThunk(
  "mlmTree/removeNode",
  async (nodeId, { rejectWithValue }) => {
    try {
      const res = await api.delete(`/api/admin/mlm-tree/${nodeId}`);
      return { nodeId, ...res.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to remove node" }
      );
    }
  }
);

// -------------------- Slice --------------------

const mlmTreeSlice = createSlice({
  name: "mlmTree",
  initialState: {
    tree: [],
    flatNodes: [],
    levels: [],
    availableUsers: [],
    totalNodes: 0,
    selectedNode: null,
    status: "idle",
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearTree: (state) => {
      state.tree = [];
      state.flatNodes = [];
      state.levels = [];
      state.availableUsers = [];
      state.totalNodes = 0;
      state.selectedNode = null;
    },
    setSelectedNode: (state, action) => {
      state.selectedNode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Tree
      .addCase(fetchTree.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchTree.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.tree = action.payload.tree;
        state.levels = action.payload.levels;
        state.totalNodes = action.payload.total_nodes;
      })
      .addCase(fetchTree.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      })

      // Fetch Flat
      .addCase(fetchFlatTree.fulfilled, (state, action) => {
        state.flatNodes = action.payload;
      })

      // Available Users
      .addCase(fetchAvailableUsers.fulfilled, (state, action) => {
        state.availableUsers = action.payload;
      })

      // Assign
      .addCase(assignNode.pending, (state) => {
        state.status = "loading";
      })
      .addCase(assignNode.fulfilled, (state) => {
        state.status = "succeeded";
        // Refetch tree after assignment
      })
      .addCase(assignNode.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      })

      // Update
      .addCase(updateNode.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateNode.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(updateNode.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      })

      // Remove
      .addCase(removeNode.pending, (state) => {
        state.status = "loading";
      })
      .addCase(removeNode.fulfilled, (state) => {
        state.status = "succeeded";
        state.selectedNode = null;
      })
      .addCase(removeNode.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      });
  },
});

export const { clearError, clearTree, setSelectedNode } = mlmTreeSlice.actions;
export default mlmTreeSlice.reducer;
